// Shared authenticated API client for both portals. One instance is created
// per app by the auth hook, which owns the bearer token: API calls read it at
// request time via the client's token store, so a refreshed token reaches
// in-flight retries and later calls without any React re-render. The client
// portal also sets an act-as tenant (x-act-as-tenant on every request) so an
// admin managing a client on their behalf reaches the right tenant without
// changing call sites.

export interface ApiRequestOptions {
  method?: string;
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

// Transparent token refresh: when a request comes back 401 (expired token), the
// registered handler is asked for a fresh token and the request is retried once
// with it. This keeps saves resilient — a token that expired mid-edit refreshes
// under the hood instead of failing the save. Handlers: supabase.refreshSession
// for a normal login, the act-as iframe relay for impersonation.
type AuthRefreshHandler = () => Promise<string | null>;

// Fired when a request is still 401 after the refresh attempt (or none was
// possible): the token is expired/rejected and could not be renewed silently.
// The client portal uses this to force the act-as expiry modal open however
// the failure was noticed — background-throttled timers, a discarded tab
// reloading with a stale stored token, or local clock skew.
type AuthExpiredListener = () => void;

type TokenListener = (token: string) => void;

export interface ApiClient {
  /** The API origin, for the rare caller that builds a URL rather than a request. */
  readonly baseUrl: string;

  /** JSON request; throws Error(message) on a non-2xx response. */
  request<T = unknown>(path: string, options?: ApiRequestOptions): Promise<T>;
  /** Multipart upload variant — lets the browser set the multipart boundary. */
  upload<T = unknown>(path: string, formData: FormData): Promise<T>;
  /**
   * Raw authenticated fetch (auth + act-as headers) for non-JSON responses,
   * e.g. file downloads. The caller owns status handling and body reading.
   */
  fetchRaw(path: string, options?: ApiRequestOptions): Promise<Response>;

  /** Replace the bearer token; notifies token subscribers. */
  setToken(token: string): void;
  /** The current bearer token ("" before sign-in / after sign-out). */
  getToken(): string;
  /**
   * Subscribe to token replacements (sign-in, silent refresh, act-as relay).
   * Returns the unsubscribe function.
   */
  subscribeToken(listener: TokenListener): () => void;

  setActAsTenant(tenantId: string | null): void;
  setAuthRefreshHandler(handler: AuthRefreshHandler | null): void;
  setAuthExpiredListener(listener: AuthExpiredListener | null): void;

  /** Clear token, act-as tenant, handlers and subscribers (sign-out). */
  reset(): void;
}

/** Error-envelope shape shared by every API error response. */
interface ApiErrorBody {
  error?: { message?: string };
}

async function readJsonSafely(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function errorMessage(body: unknown, fallback: string): string {
  return (body as ApiErrorBody | null)?.error?.message ?? fallback;
}

export function createApiClient({ baseUrl }: { baseUrl: string }): ApiClient {
  let token = "";
  let actAsTenantId: string | null = null;
  let authRefreshHandler: AuthRefreshHandler | null = null;
  let authExpiredListener: AuthExpiredListener | null = null;
  let inFlightRefresh: Promise<string | null> | null = null;
  const tokenListeners = new Set<TokenListener>();

  function setToken(next: string): void {
    if (next === token) return;
    token = next;
    for (const listener of tokenListeners) listener(next);
  }

  function actAsHeaders(): Record<string, string> {
    return actAsTenantId ? { "x-act-as-tenant": actAsTenantId } : {};
  }

  /** Refresh the auth token, de-duplicating concurrent 401s onto one refresh. */
  function refreshAuthToken(): Promise<string | null> {
    if (!authRefreshHandler) return Promise.resolve(null);
    if (!inFlightRefresh) {
      inFlightRefresh = Promise.resolve()
        .then(() => authRefreshHandler!())
        .catch(() => null)
        .then((fresh) => {
          // Adopt the fresh token so later calls (and token subscribers, e.g.
          // the act-as expiry modal) see it without the handler having to.
          if (fresh) setToken(fresh);
          return fresh;
        })
        .finally(() => {
          inFlightRefresh = null;
        });
    }
    return inFlightRefresh;
  }

  /**
   * Run `send(token)`; on a 401, refresh the token once and retry with the
   * fresh one. Returns the final Response (the caller owns status/body
   * handling).
   */
  async function withTokenRetry(send: (authToken: string) => Promise<Response>): Promise<Response> {
    const initialToken = token;
    let response = await send(initialToken);
    if (response.status !== 401) return response;
    const fresh = await refreshAuthToken();
    if (fresh && fresh !== initialToken) {
      response = await send(fresh);
    }
    if (response.status === 401) authExpiredListener?.();
    return response;
  }

  async function request<T = unknown>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const url = new URL(path, baseUrl);
    const response = await withTokenRetry((authToken) =>
      fetch(url, {
        ...options,
        headers: {
          authorization: `Bearer ${authToken}`,
          "content-type": "application/json",
          ...actAsHeaders(),
          ...options.headers,
        },
      })
    );

    const body = await readJsonSafely(response);

    if (!response.ok) {
      throw new Error(errorMessage(body, `API request failed (${response.status})`));
    }

    return body as T;
  }

  async function upload<T = unknown>(path: string, formData: FormData): Promise<T> {
    const url = new URL(path, baseUrl);
    const response = await withTokenRetry((authToken) =>
      fetch(url, {
        method: "POST",
        headers: { authorization: `Bearer ${authToken}`, ...actAsHeaders() },
        body: formData,
      })
    );

    const body = await readJsonSafely(response);

    if (!response.ok) {
      throw new Error(errorMessage(body, `Upload failed (${response.status})`));
    }

    return body as T;
  }

  function fetchRaw(path: string, options: ApiRequestOptions = {}): Promise<Response> {
    const url = new URL(path, baseUrl);
    return withTokenRetry((authToken) =>
      fetch(url, {
        ...options,
        headers: {
          authorization: `Bearer ${authToken}`,
          ...actAsHeaders(),
          ...options.headers,
        },
      })
    );
  }

  return {
    baseUrl,
    request,
    upload,
    fetchRaw,
    setToken,
    getToken: () => token,
    subscribeToken(listener) {
      tokenListeners.add(listener);
      return () => tokenListeners.delete(listener);
    },
    setActAsTenant(tenantId) {
      actAsTenantId = tenantId || null;
    },
    setAuthRefreshHandler(handler) {
      authRefreshHandler = handler;
    },
    setAuthExpiredListener(listener) {
      authExpiredListener = listener;
    },
    reset() {
      token = "";
      actAsTenantId = null;
      authRefreshHandler = null;
      authExpiredListener = null;
      tokenListeners.clear();
    },
  };
}
